import {verifiedFetch} from '@github-ui/verified-fetch'
import {LinkExternalIcon} from '@primer/octicons-react'
import {Dialog, FormControl, TextInput} from '@primer/react'
import emojiRegex from 'emoji-regex'
import type React from 'react'
import {useCallback, useState} from 'react'

import styles from './McpServerDialog.module.css'

const MCP_SPEC_LINK = (
  <a
    href="https://modelcontextprotocol.io/specification/2025-06-18/basic/authorization"
    target="_blank"
    rel="noopener noreferrer"
    style={{textDecoration: 'underline'}}
  >
    MCP authorization specification
  </a>
)

export interface McpServerDialogProps {
  // Control when dialog shows
  isOpen: boolean
  onClose: () => void

  // Optional return URL for after OAuth flow
  returnUrl?: string

  // Optional callback for when server is successfully added
  onServerAdded?: () => void

  // Optional ref for focus management
  returnFocusRef?: React.RefObject<HTMLElement>

  // Whether to show the settings link in the disclaimer
  showSettingsLink?: boolean
}

export const McpServerDialog: React.FC<McpServerDialogProps> = ({
  isOpen,
  onClose,
  returnUrl,
  onServerAdded,
  returnFocusRef,
  showSettingsLink = true,
}) => {
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [urlError, setUrlError] = useState<string | React.ReactElement | null>(null)
  const [nameError, setNameError] = useState<string | React.ReactElement | null>(null)

  // When dialog closes, reset form
  const onDialogClose = useCallback(() => {
    setName('')
    setUrl('')
    setUrlError(null)
    setNameError(null)
    onClose()
  }, [onClose])

  function isValidHttpsUrl(inputUrl: string): boolean {
    try {
      const parsedUrl = new URL(inputUrl.trim(), window.location.origin)
      const emojiRegexInstance = emojiRegex()
      return (
        parsedUrl.protocol === 'https:' &&
        !!parsedUrl.hostname &&
        /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(parsedUrl.hostname) &&
        // Check inputUrl for emojis since parsedUrl.hostname converts them to punycode
        !emojiRegexInstance.test(inputUrl)
      )
    } catch {
      return false
    }
  }

  const isFormValid = () => {
    return name.trim() !== '' && url.trim() !== ''
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }
    if (!isFormValid()) {
      // Show validation errors
      if (!url.trim()) {
        setUrlError('MCP server URL is required')
      }
      if (!name.trim()) {
        setNameError('Name is required')
      }
      return
    }

    try {
      setUrlError(null)

      // Validate HTTPS URL format before sending to backend
      if (!isValidHttpsUrl(url)) {
        setUrlError('Invalid HTTPS URL format')
        return
      }

      // Send MCP server details to backend for OAuth setup
      const formData = new FormData()
      formData.append('mcp_server_url', url.trim())
      formData.append('name', name.trim())

      // Add return URL if provided (used by MCP settings page)
      if (returnUrl) {
        formData.append('return_url', returnUrl)
      }

      const response = await verifiedFetch('/copilot/mcp/servers', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        let errorMessage: string | React.ReactElement | null = 'Failed to add MCP server'

        try {
          // Read response body once as text, then try to parse as JSON
          const responseText = await response.text()

          try {
            const errorData = JSON.parse(responseText)
            if (errorData.error === 'unsupported_server_type') {
              errorMessage = <>This server does not conform to the {MCP_SPEC_LINK}.</>
            } else if (errorData.field_errors) {
              // Loop over field errors, setting specific error messages
              // Shape is { field_errors: { fieldName: []string } }
              for (const [field, messages] of Object.entries(errorData.field_errors)) {
                const message = (messages as string[]).join(', ')
                if (field === 'mcp_server_url') {
                  setUrlError(message)
                } else if (field === 'display_name') {
                  setNameError(message)
                }
              }
              return
            } else if (errorData.message) {
              errorMessage = errorData.message
            }
          } catch {
            // If JSON parsing fails, check for specific error patterns
            if (
              responseText.includes('well-known/oauth-authorization-server') ||
              responseText.trim() === 'Client setup failed'
            ) {
              errorMessage = <>This server does not conform to the {MCP_SPEC_LINK}.</>
            } else {
              errorMessage = responseText || errorMessage
            }
          }
        } catch {
          // If we can't read response at all, use fallback message
          errorMessage = 'Failed to add MCP server'
        }

        setUrlError(errorMessage)
        return
      }

      // Success: redirect to OAuth authorization flow
      const data = await response.json()
      const redirectUrl = data.redirect_url
      if (redirectUrl && typeof redirectUrl === 'string') {
        onDialogClose()
        onServerAdded?.() // Call callback if provided
        window.location.assign(redirectUrl)
      } else {
        onDialogClose()
        onServerAdded?.() // Call callback if provided
      }
    } catch (error: unknown) {
      // Handle unexpected errors (network, parsing, etc.)
      if (error instanceof Error) {
        setUrlError(error.message)
      } else {
        setUrlError('An unknown error occurred')
      }
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <Dialog
      title="Add MCP server"
      onClose={onDialogClose}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: onDialogClose},
        {
          buttonType: 'primary',
          content: (
            <>
              Connect to MCP server
              <span style={{marginLeft: 8}}>
                <LinkExternalIcon size={16} />
              </span>
            </>
          ),
          onClick: () => {
            void handleSubmit()
          },
        },
      ]}
      returnFocusRef={returnFocusRef}
    >
      <form className={styles.dialogForm} onSubmit={handleSubmit} autoComplete="on">
        <FormControl required className={styles.formField}>
          <FormControl.Label>MCP server name</FormControl.Label>
          <TextInput
            name="mcp-server-name"
            type="text"
            value={name}
            onChange={e => {
              setName(e.target.value)
              setNameError(null)
            }}
            placeholder="Enter a name for this MCP server"
            className={nameError ? styles.inputError : undefined}
            autoComplete="on"
            block
          />
          {nameError && <FormControl.Validation variant="error">{nameError}</FormControl.Validation>}
        </FormControl>
        <FormControl required className={styles.formField}>
          <FormControl.Label>MCP server URL</FormControl.Label>
          <TextInput
            name="mcp-url"
            type="url"
            value={url}
            onChange={e => {
              setUrl(e.target.value)
              setUrlError(null)
            }}
            placeholder="https://mcp.example.com"
            className={urlError ? styles.inputError : undefined}
            autoComplete="on"
            block
          />
          {urlError && <FormControl.Validation variant="error">{urlError}</FormControl.Validation>}
        </FormControl>
        {/* Hidden submit button required for Enter key submission */}
        <button type="submit" className={styles.hiddenSubmitButton} tabIndex={-1}>
          Submit
        </button>
        <p>
          MCP Server data will be processed by models coming from third-party providers. Always review the permissions
          required by the MCP server and only connect to MCP servers you know and trust.
          {showSettingsLink && (
            <>
              {' '}
              You can revoke access for the MCP server at any time in{' '}
              <a href="/settings" className={styles.underline}>
                your settings
              </a>
              .
            </>
          )}
        </p>
      </form>
    </Dialog>
  )
}

try{ MCP_SPEC_LINK.displayName ||= 'MCP_SPEC_LINK' } catch {}
try{ McpServerDialog.displayName ||= 'McpServerDialog' } catch {}